import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { useScrollReveal } from '../hooks/useAnimations';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReviewCard({ review }) {
  return (
    <div className="flex-shrink-0 w-[320px] bg-white p-5 rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-shadow duration-300 mx-2">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#4285F4] to-[#34A853] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
          {review.profile_photo}
        </div>
        <div className="min-w-0">
          <p className="font-manrope font-medium text-[#0F172A] text-sm truncate">{review.name}</p>
          <p className="text-[10px] text-[#94A3B8] font-manrope">{review.time_ago}</p>
        </div>
      </div>
      <div className="flex gap-0.5 mb-2">
        {[...Array(review.rating)].map((_, i) => (
          <Star key={i} className="w-3 h-3 text-[#FBBC05] fill-[#FBBC05]" />
        ))}
      </div>
      <p className="text-xs text-[#475569] font-manrope leading-relaxed line-clamp-3">{review.text}</p>
    </div>
  );
}

function MarqueeRow({ reviews, direction = 'left', speed = 40 }) {
  // Duplicate for seamless loop
  const items = [...reviews, ...reviews, ...reviews];
  const animName = direction === 'left' ? 'marqueeLeft' : 'marqueeRight';

  return (
    <div className="overflow-hidden relative">
      {/* Edge fades */}
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#FAFAFA] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#FAFAFA] to-transparent z-10 pointer-events-none" />
      <div
        className="flex"
        style={{
          animation: `${animName} ${speed}s linear infinite`,
          width: 'max-content',
        }}
      >
        {items.map((review, idx) => (
          <ReviewCard key={`${review.id}-${idx}`} review={review} />
        ))}
      </div>
    </div>
  );
}

export default function GoogleReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [headerRef, headerVisible] = useScrollReveal(0.2);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/google-reviews`);
        setReviews(res.data);
      } catch (err) {
        // Reviews fetch failed silently
      }
    };
    fetchReviews();
  }, []);

  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '4.9';

  const topRow = reviews.slice(0, Math.ceil(reviews.length / 2));
  const bottomRow = reviews.slice(Math.ceil(reviews.length / 2));

  return (
    <section className="py-16 md:py-24 bg-[#FAFAFA] overflow-hidden" data-testid="google-reviews-section">
      {/* Header */}
      <div
        ref={headerRef}
        className={`max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between mb-10 gap-6 transition-all duration-700 ${
          headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2.5">
            <svg className="w-8 h-8" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="font-outfit font-medium text-[#0F172A] text-lg">Google Reviews</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-outfit font-semibold text-3xl text-[#0F172A]">{avgRating}</span>
            <div>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 text-[#FBBC05] fill-[#FBBC05]" />
                ))}
              </div>
              <p className="text-xs text-[#475569] font-manrope">{reviews.length}+ reviews</p>
            </div>
          </div>
        </div>
        <a
          href="https://g.page/r/CVeIH6aZqTIREBM/review"
          target="_blank"
          rel="noopener noreferrer"
          className="px-5 py-2.5 rounded-full border-2 border-[#4285F4] text-[#4285F4] text-sm font-manrope font-medium hover:bg-[#4285F4] hover:text-white transition-all duration-300"
          data-testid="write-review-btn"
        >
          Write a Review
        </a>
      </div>

      {/* Animated Marquee Rows */}
      <div className="space-y-4" data-testid="google-reviews-marquee">
        {topRow.length > 0 && <MarqueeRow reviews={topRow} direction="left" speed={35} />}
        {bottomRow.length > 0 && <MarqueeRow reviews={bottomRow} direction="right" speed={40} />}
      </div>

      {/* Marquee Keyframes */}
      <style>{`
        @keyframes marqueeLeft {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes marqueeRight {
          0% { transform: translateX(-33.333%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </section>
  );
}
